import type {
  WorkflowPresentationInfo,
  WorkflowPresentationViewport,
} from '@affine/core/modules/editor/services/workspace-realtime.service';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor/services/workspace-realtime.service';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { LiveData, Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

import {
  isWorkflowPresenceVersionKey,
  versionPresenceKey,
  WorkflowLivePresenceService,
} from './workflow-live-presence';

export interface WorkflowLivePresentationLocalRole {
  role: 'moderator' | 'follower';
  workflowId: string;
  versionKey: string;
  moderatorClientId?: string;
}

const HEARTBEAT_INTERVAL_MS = 3000;

export function workflowPresentationKey(
  workflowId: string,
  versionKey: string
): string {
  if (!isWorkflowPresenceVersionKey(versionKey)) {
    return `${workflowId}\u001f${versionKey}`;
  }
  return versionPresenceKey(workflowId, versionKey);
}

export class WorkflowLivePresentationService extends Service {
  private readonly flagSubscription: Subscription;
  private readonly followFailedSubscription: Subscription;
  private readonly presentationsSubscription: Subscription;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  readonly enabled$ =
    this.featureFlagService.flags.enable_workflow_live_collaboration.$;

  readonly localRole$ = new LiveData<WorkflowLivePresentationLocalRole | null>(
    null
  );

  readonly viewport$ = this.realtimeService.workflowPresentationViewport$;
  readonly followFailed$ =
    this.realtimeService.workflowPresentationFollowFailed$;

  readonly presentations$ = LiveData.computed<WorkflowPresentationInfo[]>(
    get => {
      if (!get(this.enabled$)) return [];
      return get(this.realtimeService.workflowPresentations$);
    }
  );

  readonly presentationsByVersion$ = this.presentations$.map(presentations => {
    return new Map(
      presentations.map(presentation => [
        workflowPresentationKey(
          presentation.workflowId,
          presentation.versionKey
        ),
        presentation,
      ])
    );
  });

  constructor(
    private readonly realtimeService: WorkspaceRealtimeService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly presenceService: WorkflowLivePresenceService
  ) {
    super();
    this.flagSubscription = this.enabled$.subscribe(enabled => {
      if (!enabled) {
        this.stopHeartbeat();
        this.clearLocalRoleFocus({ emitPresence: false });
        this.realtimeService.clearWorkflowPresentationLocalState();
      }
    });
    this.followFailedSubscription = this.followFailed$.subscribe(event => {
      const localRole = this.localRole$.value;
      if (!event) return;
      if (
        localRole?.role === 'follower' &&
        localRole.workflowId === event.workflowId &&
        localRole.versionKey === event.versionKey
      ) {
        this.clearLocalRoleFocus({ emitPresence: true });
      }
    });
    this.presentationsSubscription = this.presentations$.subscribe(
      presentations => {
        const localRole = this.localRole$.value;
        if (!localRole || !this.realtimeService.isConnected$.value) return;
        const active = presentations.some(
          presentation =>
            presentation.workflowId === localRole.workflowId &&
            presentation.versionKey === localRole.versionKey
        );
        if (!active) {
          this.clearLocalRoleFocus({ emitPresence: true });
        }
      }
    );
  }

  get enabled(): boolean {
    return this.enabled$.value === true;
  }

  getCurrentUserId(): string | null {
    return this.realtimeService.getCurrentUserId();
  }

  async refreshWorkflow(workflowId: string): Promise<void> {
    if (!this.enabled) return;
    await this.realtimeService.refreshWorkflowPresentations(workflowId);
  }

  async startModerating(
    workflowId: string,
    versionKey: string,
    viewport?: WorkflowPresentationViewport
  ): Promise<boolean> {
    if (!this.enabled) return false;
    const result = await this.realtimeService.startWorkflowPresentation(
      workflowId,
      versionKey,
      viewport
    );
    if (!result.success) return false;
    this.localRole$.next({ role: 'moderator', workflowId, versionKey });
    this.presenceService.updateWorkflowVersion(workflowId, versionKey, {
      focus: 'moderating',
    });
    this.startHeartbeat(workflowId, versionKey);
    return true;
  }

  async stopModerating(workflowId: string, versionKey: string): Promise<void> {
    if (!this.enabled) {
      this.clearLocalRoleFocus({
        emitPresence: false,
        workflowId,
        versionKey,
      });
      return;
    }
    this.stopHeartbeat();
    await this.realtimeService.stopWorkflowPresentation(workflowId, versionKey);
    this.clearLocalRoleFocus({ emitPresence: true, workflowId, versionKey });
  }

  updateViewport(
    workflowId: string,
    versionKey: string,
    viewport: WorkflowPresentationViewport
  ): void {
    if (!this.enabled) return;
    const localRole = this.localRole$.value;
    if (
      localRole?.role !== 'moderator' ||
      localRole.workflowId !== workflowId ||
      localRole.versionKey !== versionKey
    ) {
      return;
    }
    this.realtimeService.updateWorkflowPresentation(
      workflowId,
      versionKey,
      viewport
    );
  }

  async follow(session: WorkflowPresentationInfo): Promise<void> {
    if (!this.enabled) return;
    this.localRole$.next({
      role: 'follower',
      workflowId: session.workflowId,
      versionKey: session.versionKey,
      moderatorClientId: session.moderatorClientId,
    });
    this.presenceService.updateWorkflowVersion(
      session.workflowId,
      session.versionKey,
      {
        focus: 'following',
      }
    );
    await this.realtimeService.followWorkflowPresentation(
      session.workflowId,
      session.versionKey,
      session.moderatorClientId
    );
  }

  async unfollow(workflowId: string, versionKey: string): Promise<void> {
    if (!this.enabled) {
      this.clearLocalRoleFocus({
        emitPresence: false,
        workflowId,
        versionKey,
      });
      return;
    }
    await this.realtimeService.unfollowWorkflowPresentation(
      workflowId,
      versionKey
    );
    this.clearLocalRoleFocus({ emitPresence: true, workflowId, versionKey });
  }

  private startHeartbeat(workflowId: string, versionKey: string): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.realtimeService.heartbeatWorkflowPresentation(
        workflowId,
        versionKey
      );
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (!this.heartbeatTimer) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private clearLocalRoleFocus({
    emitPresence,
    workflowId,
    versionKey,
  }: {
    emitPresence: boolean;
    workflowId?: string;
    versionKey?: string;
  }): void {
    const localRole = this.localRole$.value;
    if (
      localRole &&
      ((workflowId && localRole.workflowId !== workflowId) ||
        (versionKey && localRole.versionKey !== versionKey))
    ) {
      return;
    }
    this.localRole$.next(null);
    if (!emitPresence || !localRole) return;
    this.presenceService.updateWorkflowVersion(
      localRole.workflowId,
      localRole.versionKey,
      {
        focus: null,
      }
    );
  }

  override dispose(): void {
    const localRole = this.localRole$.value;
    if (this.enabled && localRole?.role === 'moderator') {
      void this.realtimeService.stopWorkflowPresentation(
        localRole.workflowId,
        localRole.versionKey
      );
    } else if (this.enabled && localRole?.role === 'follower') {
      void this.realtimeService.unfollowWorkflowPresentation(
        localRole.workflowId,
        localRole.versionKey
      );
    }
    this.stopHeartbeat();
    this.clearLocalRoleFocus({ emitPresence: this.enabled });
    if (!this.enabled) {
      this.realtimeService.clearWorkflowPresentationLocalState();
    }
    this.flagSubscription.unsubscribe();
    this.followFailedSubscription.unsubscribe();
    this.presentationsSubscription.unsubscribe();
    super.dispose();
  }
}
